/**
 * Table Pagination Component
 * Reusable pagination controls for tables with page size selection
 */

import React from 'react';
import { makeStyles, Button, Text, Dropdown, Option } from '@fluentui/react-components';
import {
  ChevronLeftRegular,
  ChevronRightRegular,
  ChevronDoubleLeftRegular,
  ChevronDoubleRightRegular,
} from '@fluentui/react-icons';

const useStyles = makeStyles({
  paginationContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 20px',
    borderTop: '1px solid #e1dfdd',
    backgroundColor: '#faf9f8',
    gap: '16px',
    flexWrap: 'wrap',
  },
  infoSection: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    flex: '1 1 auto',
  },
  pageInfo: {
    fontSize: '14px',
    color: '#605e5c',
    whiteSpace: 'nowrap',
  },
  controlsSection: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    flex: '0 0 auto',
  },
  pageButtons: {
    display: 'flex',
    gap: '4px',
    alignItems: 'center',
  },
  pageSizeSelector: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  dropdown: {
    minWidth: '80px',
  },
  pageButton: {
    minWidth: '32px',
    height: '32px',
    padding: '0 8px',
  },
  currentPageText: {
    fontSize: '14px',
    color: '#323130',
    fontWeight: '600',
    padding: '0 8px',
    minWidth: '32px',
    textAlign: 'center',
  },
});

export interface TablePaginationProps {
  totalItems: number;
  currentPage: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
  onItemsPerPageChange: (itemsPerPage: number) => void;
  pageSizeOptions?: number[];
}

export const TablePagination: React.FC<TablePaginationProps> = ({
  totalItems,
  currentPage,
  itemsPerPage,
  onPageChange,
  onItemsPerPageChange,
  pageSizeOptions = [10, 20, 50, 100, 150, 200],
}) => {
  const styles = useStyles();

  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startItem = totalItems === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      onPageChange(newPage);
    }
  };

  const handleItemsPerPageChange = (value: string) => {
    const newSize = parseInt(value, 10);
    if (!isNaN(newSize)) {
      onItemsPerPageChange(newSize);
      // Reset to first page when changing page size
      onPageChange(1);
    }
  };

  return (
    <div className={styles.paginationContainer}>
      {/* Info Section */}
      <div className={styles.infoSection}>
        <Text className={styles.pageInfo}>
          Showing {startItem}-{endItem} of {totalItems} records
        </Text>
      </div>

      {/* Controls Section */}
      <div className={styles.controlsSection}>
        {/* Page Size Selector */}
        <div className={styles.pageSizeSelector}>
          <Text className={styles.pageInfo}>Rows per page:</Text>
          <Dropdown
            className={styles.dropdown}
            value={String(itemsPerPage)}
            selectedOptions={[String(itemsPerPage)]}
            onOptionSelect={(e, data) => {
              if (data.optionValue) {
                handleItemsPerPageChange(data.optionValue);
              }
            }}
            size="small"
          >
            {pageSizeOptions.map(size => (
              <Option key={size} value={String(size)} text={String(size)}>
                {size}
              </Option>
            ))}
          </Dropdown>
        </div>

        {/* Page Navigation */}
        <div className={styles.pageButtons}>
          {/* First Page */}
          <Button
            className={styles.pageButton}
            appearance="subtle"
            size="small"
            icon={<ChevronDoubleLeftRegular />}
            onClick={() => handlePageChange(1)}
            disabled={currentPage === 1 || totalPages === 0}
            title="First page"
          />

          {/* Previous Page */}
          <Button
            className={styles.pageButton}
            appearance="subtle"
            size="small"
            icon={<ChevronLeftRegular />}
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1 || totalPages === 0}
            title="Previous page"
          />

          {/* Current Page Display */}
          <Text className={styles.currentPageText}>
            {totalPages === 0 ? '0' : currentPage} / {totalPages}
          </Text>

          {/* Next Page */}
          <Button
            className={styles.pageButton}
            appearance="subtle"
            size="small"
            icon={<ChevronRightRegular />}
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages || totalPages === 0}
            title="Next page"
          />

          {/* Last Page */}
          <Button
            className={styles.pageButton}
            appearance="subtle"
            size="small"
            icon={<ChevronDoubleRightRegular />}
            onClick={() => handlePageChange(totalPages)}
            disabled={currentPage === totalPages || totalPages === 0}
            title="Last page"
          />
        </div>
      </div>
    </div>
  );
};
